"use client";

import { SquareTerminalIcon, TerminalIcon } from "lucide-react";

import type {
  ShellCallContentBlock,
  ShellCallOutputContentBlock,
} from "../../types";
import { cn, truncate } from "../../utils";
import { CollapsibleSection } from "../CollapsibleSection";

// ---------------------------------------------------------------------------
// ShellCallBlock
// ---------------------------------------------------------------------------

interface ShellCallBlockProps {
  block: ShellCallContentBlock;
  isStreaming?: boolean;
}

export const ShellCallBlock = ({ block, isStreaming }: ShellCallBlockProps) => {
  const action = block.action as { commands?: string[] };
  const commands = action?.commands ?? [];
  const code = commands.join("\n") || "Shell command";
  const lines = code.split("\n");
  const previewLines = lines.slice(0, 3).map((l) => truncate(l));
  const preview = previewLines.join(" | ") + (lines.length > 3 ? " ..." : "");

  return (
    <CollapsibleSection
      icon={<TerminalIcon className="h-3 w-3" />}
      label="Shell"
      preview={preview}
      copyContent={code}
      isStreaming={isStreaming}
    >
      <pre className="overflow-x-auto rounded-md bg-zinc-100 p-2 text-xs text-zinc-800">
        {code}
      </pre>
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// ShellCallOutputBlock
// ---------------------------------------------------------------------------

interface ShellCallOutputBlockProps {
  block: ShellCallOutputContentBlock;
  isStreaming?: boolean;
}

export const ShellCallOutputBlock = ({
  block,
  isStreaming,
}: ShellCallOutputBlockProps) => {
  const parts: string[] = [];
  for (const entry of block.output ?? []) {
    if (entry.stdout) {
      parts.push(entry.stdout);
    }
    if (entry.stderr) {
      parts.push(`STDERR: ${entry.stderr}`);
    }
  }
  const output = parts.join("\n") || "(no output)";

  const exitEntry = (block.output ?? []).find(
    (e) => e.outcome?.type === "exit",
  );
  const exitCode = exitEntry?.outcome?.exit_code;
  const isSuccess = exitCode === 0 || exitCode === undefined;
  const preview = truncate(output);

  return (
    <CollapsibleSection
      icon={<SquareTerminalIcon className="h-3 w-3" />}
      label="Shell Output"
      preview={preview}
      copyContent={output}
      isStreaming={isStreaming}
    >
      <div className="flex flex-col gap-1.5">
        <span
          className={cn(
            "inline-flex w-fit rounded-full px-2 py-0.5 text-[10px] font-medium",
            isSuccess ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600",
          )}
        >
          {isSuccess ? "success" : `exit ${exitCode}`}
        </span>
        <pre className="overflow-x-auto rounded-md bg-zinc-100 p-2 font-mono text-xs text-zinc-800">
          {output}
        </pre>
      </div>
    </CollapsibleSection>
  );
};
